import React, {FC} from 'react'
import {useIntl} from 'react-intl'
import styled from '@emotion/styled'
import {Review} from '@findhotel/sapi'
import {differenceInMonths} from 'date-fns/differenceInMonths'
import {intlFormat} from 'date-fns/intlFormat'
import {intlFormatDistance} from 'date-fns/intlFormatDistance'
import {parseISO} from 'date-fns/parseISO'

import {Box} from '@vio/atlas/helpers/Box'
import {ScoreRating} from '@vio/atlas/ScoreRating'
import {Text} from '@vio/atlas/Text'

import {ProviderLogo} from '../../search/ProviderLogo'

export interface ReviewCommentProps
  extends Pick<Review, 'reviewerScore' | 'reviewerName' | 'reviewDate'> {
  /** Length of stay */
  lengthOfStay?: number
  /** Slot to display review source logo if any */
  SourceLogoSlot?: React.ReactNode
  /** The logo click handler */
  onSourceLogoClick?: (e: React.SyntheticEvent) => void
  /** The provider review source */
  reviewSource?: string
  /** User language code */
  languageCode?: string
}

const Info = styled.div`
  position: relative;
`

interface SubLabelProps {
  reviewSource: ReviewCommentProps['reviewSource']
  displayDate: ReviewCommentProps['reviewDate']
  lengthOfStayString: string
  onSourceLogoClick: ReviewCommentProps['onSourceLogoClick']
}
const SubLabel: React.FC<SubLabelProps> = ({
  reviewSource,
  displayDate,
  lengthOfStayString,
  onSourceLogoClick
}) => (
  <Box display="flex" alignItems="center" gap="s200" whiteSpace="nowrap">
    {reviewSource && (
      <Box onClick={onSourceLogoClick}>
        <ProviderLogo variant="icon" providerCode={reviewSource} height={12} />
      </Box>
    )}
    <Text variant="bodyXS">{displayDate}</Text>
    <Text variant="bodyXS">·</Text>
    <Text variant="bodyXS">{lengthOfStayString}</Text>
  </Box>
)

export const ReviewComment: FC<ReviewCommentProps> = ({
  children,
  reviewerScore,
  reviewerName,
  reviewDate,
  lengthOfStay,
  reviewSource,
  onSourceLogoClick,
  languageCode = 'en'
}) => {
  const {formatMessage} = useIntl()
  const reviewDateObject = parseISO(reviewDate)

  const now = new Date()
  const displayDate =
    differenceInMonths(now, reviewDateObject) >= 1
      ? intlFormat(
          reviewDateObject,
          {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
          },
          {locale: languageCode}
        )
      : intlFormatDistance(reviewDateObject, now, {
          locale: languageCode
        })

  const lengthOfStayString = lengthOfStay
    ? formatMessage(
        {
          id: 'stayedNights',
          defaultMessage:
            'Stayed {nightsCount, plural, one {1 night} other {# nights}}'
        },
        {
          nightsCount: lengthOfStay
        }
      )
    : ''

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap="s300"
      data-id="hotelReviewsCommentItem"
    >
      <Info>
        <ScoreRating
          value={reviewerScore}
          label={reviewerName}
          subLabel={
            <SubLabel
              reviewSource={reviewSource}
              displayDate={displayDate}
              lengthOfStayString={lengthOfStayString}
              onSourceLogoClick={onSourceLogoClick}
            />
          }
        />
      </Info>
      <Text variant="bodyM" as="div">
        {children}
      </Text>
    </Box>
  )
}
